import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { whatsappApi } from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { MessageCircle, MapPin, Building2, ArrowRight, X, Phone } from 'lucide-react';

const WhatsAppButton = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [pulseActive, setPulseActive] = useState(true);
  const [contactInfo, setContactInfo] = useState(null);
  const [loadingContact, setLoadingContact] = useState(false);

  // Show tooltip after 3 seconds for non-logged users
  useEffect(() => {
    if (!user) {
      const timer = setTimeout(() => {
        setShowTooltip(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  // Pulse animation every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setPulseActive(true);
      setTimeout(() => setPulseActive(false), 2000);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fetch contact info when user opens dialog and is logged in
  useEffect(() => {
    if (isOpen && user && !contactInfo) {
      setLoadingContact(true);
      whatsappApi.getContact()
        .then(data => setContactInfo(data))
        .catch(err => console.error('Error fetching contact:', err))
        .finally(() => setLoadingContact(false));
    }
  }, [isOpen, user, contactInfo]);

  const handleButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Always close tooltip first and open dialog
    setShowTooltip(false);
    setIsOpen(true);
  };

  const handleRegister = () => {
    setIsOpen(false);
    navigate('/login');
  };

  const handleContactOffice = () => {
    if (!contactInfo?.whatsapp_number) {
      return;
    }
    
    // Formatta il numero per WhatsApp
    let whatsappNumber = contactInfo.whatsapp_number.replace(/\s+/g, '').replace(/-/g, '');
    if (!whatsappNumber.startsWith('+')) {
      whatsappNumber = '+39' + whatsappNumber;
    }
    whatsappNumber = whatsappNumber.replace('whatsapp:', '');
    
    const message = encodeURIComponent(
      `Ciao! Sono ${user?.name}, cliente Energy Multiservizi. Vorrei informazioni sui vostri servizi.`
    );
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
    setIsOpen(false);
  };

  const handleCallOffice = () => {
    if (!contactInfo?.phone) return;
    
    let phoneNumber = contactInfo.phone.replace(/\s+/g, '').replace(/-/g, '');
    if (!phoneNumber.startsWith('+')) {
      phoneNumber = '+39' + phoneNumber;
    }
    window.open(`tel:${phoneNumber}`, '_self');
  };

  const handleCloseTooltip = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowTooltip(false);
  };

  return (
    <>
      {/* Floating WhatsApp Button */}
      <div className="fixed bottom-6 right-6 z-40">
        {/* Tooltip for non-logged users - positioned with pointer-events-none except for close button */}
        {showTooltip && !user && (
          <div 
            className="absolute bottom-16 right-0 w-72 animate-fade-in z-50"
            style={{ pointerEvents: 'none' }}
          >
            <div 
              className="relative bg-slate-900 border border-slate-700 rounded-xl p-4 shadow-xl"
              style={{ pointerEvents: 'auto' }}
            >
              <button 
                onClick={handleCloseTooltip}
                className="absolute top-2 right-2 text-slate-400 hover:text-white z-10"
                type="button"
              >
                <X className="w-4 h-4" />
              </button>
              <p className="text-white text-sm mb-3">
                Hai bisogno di assistenza? Contatta il tuo ufficio di fiducia via WhatsApp!
              </p>
              <p className="text-slate-400 text-xs mb-3">
                Registrati per essere indirizzato all'ufficio Energy Multiservizi più vicino a te.
              </p>
              <Button 
                size="sm" 
                className="w-full btn-gradient text-sm"
                onClick={(e) => { 
                  e.stopPropagation(); 
                  setShowTooltip(false);
                  handleRegister(); 
                }}
              >
                Registrati ora
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              {/* Arrow pointing to button */}
              <div className="absolute -bottom-2 right-6 w-4 h-4 bg-slate-900 border-r border-b border-slate-700 rotate-45"></div>
            </div>
          </div>
        )}

        {/* Main Button - ensure it's always clickable */}
        <button
          onClick={handleButtonClick}
          type="button"
          className={`
            relative flex items-center justify-center
            w-14 h-14 rounded-full
            bg-[#25D366] hover:bg-[#20BD5A]
            shadow-lg hover:shadow-xl
            transition-all duration-300
            cursor-pointer
            z-50
            ${!user ? 'opacity-80 grayscale-[30%]' : ''}
            ${pulseActive && !user ? 'animate-pulse' : ''}
          `}
          data-testid="whatsapp-float-btn"
        >
          {/* WhatsApp Icon */}
          <svg 
            className="w-7 h-7 text-white pointer-events-none" 
            fill="currentColor" 
            viewBox="0 0 24 24"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>

          {/* Inactive indicator for non-logged users */}
          {!user && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-slate-700 rounded-full flex items-center justify-center border-2 border-slate-900 pointer-events-none">
              <span className="text-[10px] text-slate-300">?</span>
            </div>
          )}

          {/* Pulse ring */}
          {pulseActive && (
            <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30 pointer-events-none"></span>
          )}
        </button>
      </div>

      {/* Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              Contattaci su WhatsApp
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {user 
                ? 'Contatta il tuo ufficio di fiducia o la sede principale.'
                : 'Registrati per essere indirizzato all\'ufficio Energy Multiservizi più adatto a te.'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {user ? (
              // Logged in user view
              <>
                {loadingContact ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-8 h-8 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin"></div>
                  </div>
                ) : (
                  <>
                    <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                      <div className="flex items-start gap-3">
                        <Building2 className="w-5 h-5 text-cyan-400 mt-0.5" />
                        <div>
                          <p className="text-white font-medium">
                            {contactInfo?.sede_name || 'ENERGY MULTISERVIZI'}
                          </p>
                          <p className="text-sm text-slate-400 mt-1">
                            {contactInfo?.is_mother_office 
                              ? 'Sede Principale - Contatta per assistenza generale'
                              : 'Il tuo ufficio di fiducia'
                            }
                          </p>
                          {contactInfo?.phone && (
                            <p className="text-sm text-cyan-400 mt-2 flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {contactInfo.phone}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      {contactInfo?.whatsapp_number ? (
                        <Button 
                          className="flex-1 bg-[#25D366] hover:bg-[#20BD5A] text-white"
                          onClick={handleContactOffice}
                          data-testid="whatsapp-contact-btn"
                        >
                          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                          WhatsApp
                        </Button>
                      ) : (
                        <div className="flex-1 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-center">
                          <p className="text-yellow-400 text-sm">Numero WhatsApp non disponibile</p>
                        </div>
                      )}
                      
                      {contactInfo?.phone && (
                        <Button 
                          variant="outline"
                          className="border-slate-700 text-white hover:bg-slate-800"
                          onClick={handleCallOffice}
                          data-testid="phone-contact-btn"
                        >
                          <Phone className="w-4 h-4 mr-2" />
                          Chiama
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </>
            ) : (
              // Not logged in view
              <>
                <div className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-cyan-400 mt-0.5" />
                    <div>
                      <p className="text-cyan-400 font-medium">
                        Perché registrarsi?
                      </p>
                      <p className="text-sm text-slate-300 mt-1">
                        La registrazione ci permette di indirizzare la tua pratica presso:
                      </p>
                      <ul className="text-sm text-slate-400 mt-2 space-y-1">
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400"></span>
                          Il tuo ufficio di fiducia (se ne hai uno)
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400"></span>
                          L'ufficio Energy Multiservizi più vicino al tuo civico
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Button 
                    className="w-full btn-gradient"
                    onClick={handleRegister}
                    data-testid="whatsapp-register-btn"
                  >
                    Registrati ora
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  
                  <p className="text-center text-xs text-slate-500">
                    Hai già un account?{' '}
                    <button 
                      onClick={handleRegister}
                      className="text-cyan-400 hover:text-cyan-300"
                    >
                      Accedi
                    </button>
                  </p>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WhatsAppButton;
